// #region: Class definition *********************************************************************/
/**
 * Custom web component that renders attachment containers with preview functionality.
 * Extends the WebComponent base class and provides attachment display, preview, and deletion features
 * with automatic cross-component communication support.
 * @class PreviewContainer
 * @extends WebComponent
 * @example
 * // Basic usage
 * <preview-container id="attachment-preview"></preview-container>
 * 
 * @example
 * // With explicit (delete) API path.
 * <preview-container id="attachment-preview" 
 *                    api-path="/api/attachments"
 * </preview-container>
 * 
 * @example
 * // Connected to file-upload component (automatic cross-referencing)
 * <file-upload id="upload-btn"
 *              api-path="/api/upload/attachments"
 *              preview-container-id="attachment-preview">
 * </file-upload>
 * <preview-container id="attachment-preview"></preview-container>
 * 
 * @property {string} id - Unique identifier for the component (required for cross-component communication).
 * @property {string} api-path - API path for (deleting) attachments.
 * @property {string} drawing-line-thickness - Line thickness for drawing (default: 3).
 * @property {boolean} stacked - Whether to render in stacked deck mode (overlapping cards).
 * 
 * @remarks
 * Cross-Component Communication:
 * - Automatically finds connected file-upload components that reference this container.
 * - Can derive (delete) API path from connected file-upload components.
 * - Dispatches events when attachments are added/removed for file count updates.
 * 
 * Delete API Path Resolution (in priority order):
 * 1. Explicit `api-path` attribute.
 * 2. Connected file-upload component's `api-path`.
 * 
 * Events Dispatched:
 * - `attachment-added`: When an attachment is added to the container.
 * - `attachment-removed`: When an attachment is successfully removed from the container.
 * 
 * Features:
 * - Attachment drawing (images).
 * - Attachment preview (images, PDFs, documents).
 * - Attachment deletion with server confirmation.
 * - Navigation between multiple attachments.
 * - Keyboard navigation (arrow keys, escape).
 * - Responsive dialog sizing.
 * - Localization support.
 * 
 * File Type Support:
 * - Images: Preview with zoom functionality.
 * - PDFs: Embedded PDF viewer.
 * - Documents: Icon-based display with download links.
 */
class PreviewContainer extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
     * Minimum height allowed for the preview dialog.
     * @type {number}
     * 
     * @static
     * @memberof PreviewContainer
     */
    static MIN_PREVIEW_HEIGHT = 700;

    /**
     * Minimum width allowed for the preview dialog.
     * @type {number}
     * 
     * @static
     * @memberof PreviewContainer
     */
    static MIN_PREVIEW_WIDTH = 900;

    /**
     * The time after the preview-item card (in stacked view) bounces back to its original state.
     * @type {number}
     * 
     * @static
     * @memberof PreviewContainer
     */
    static TOUCH_TIMEOUT = 3000;
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * Container element for attachment items.
     * @type {HTMLElement|null}
     * 
     * @private
     * @memberof PreviewContainer
     */
    #attachmentContainer;

    /**
     * Drawing functionality instance.
     * @type {Object|null}
     * 
     * @private
     * @memberof PreviewContainer
     */
    #drawingHandler;

    /**
     * Map of GUIDs to attachment elements.
     * @type {Map<string, HTMLElement>}
     * 
     * @private
     * @memberof PreviewContainer
     */
    #guidToElementMap;

    /**
     * Guid of the inspection, when relevant.
     * @type {string|null}
     * 
     * @private
     * @memberof PreviewContainer
     */
    #inspectionGuid;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     *
     * @private
     * @memberof PreviewContainer
     */
    #locales;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Initializes a new PreviewContainer instance.
     * Sets up internal state and registers locale namespaces.
     * 
     * @constructor
     * @memberof PreviewContainer
     */
    constructor() {
        super();

        this.#attachmentContainer = null;
        this.#drawingHandler = null;
        this.#guidToElementMap = new Map();

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * @type {string}
     * 
     * @static
     * @memberof PreviewContainer
     */
    static tagName = 'preview-container';

    /**
     * Returns the string representation of this class instance.
     * @returns {string} The string representation of this class.
     * 
     * @memberof PreviewContainer
     */
    toString() {
        return '[PreviewContainer]';
    }

    /**
     * Returns the string representation of this class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof PreviewContainer
     */
    static toString() {
        return '[PreviewContainer]';
    }

    /**
     * Returns the version number of this class.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof PreviewContainer
     */
    static version() {
        return '2.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * API path for (deleting) attachments.
     * @returns {string|null} The (delete) API path.
     * 
     * @memberof PreviewContainer
     */
    get apiPath() {
        return this.getAttribute('api-path');
    }

    /**
     * Sets the API path for deleting attachments.
     * @param {string} value The delete API path.
     * 
     * @memberof PreviewContainer
     */
    set apiPath(value) {
        this.setAttribute('api-path', value);
    }


    /**
     * Drawing line thickness.
     * @returns {number} The drawing line thickness.
     * 
     * @memberof PreviewContainer
     */
    get drawingLineThickness() {
        return parseInt(this.getAttribute('drawing-line-thickness') || '3');
    }

    /**
     * Sets the drawing line thickness.
     * @param {number} value The drawing line thickness.
     * 
     * @memberof PreviewContainer
     */
    set drawingLineThickness(value) {
        this.setAttribute('drawing-line-thickness', value.toString());
    }

    /**
     * Whether the container should render in stacked mode.
     * @returns {boolean} True if stacked mode is enabled.
     * 
     * @memberof PreviewContainer
     */
    get stacked() {
        return this.hasAttribute('stacked');
    }

    /**
     * Sets stacked mode for the container.
     * @param {boolean} value Whether to enable stacked mode.
     * 
     * @memberof PreviewContainer
     */
    set stacked(value) {
        if (value) {
            this.setAttribute('stacked', '');
        }
        else {
            this.removeAttribute('stacked');
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Initialize the UI (on 'DOMContentLoaded').
     * @returns {void}
     * 
     * @memberof PreviewContainer
     */
    initialize() {
        this.innerHTML = '';

        this.#attachmentContainer = document.createElement('div');
        this.#attachmentContainer.className = 'preview-items';
        

        // Apply stacked class if stacked attribute is present.
        if (this.stacked) {
            this.#attachmentContainer.classList.add('stacked-view');
            this.#setupHorizontalScrolling();
            this.#updateScrollWidth();
        }

        this.appendChild(this.#attachmentContainer);
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles delete button click events.
     * @param {MouseEvent} event The click event.
     * @returns {Promise<void>}
     * 
     * @private
     * @memberof PreviewContainer
     */
    async onDeleteClick(event) {
        const container = event.target.closest('.preview-card');
        let displayNameLink = container.querySelector('.display-name');
        
        // Display-name element might not exist.
        if (!displayNameLink) {
            displayNameLink = {
                textContent: container.dataset.displayName,
                href: container.dataset.attachmentUrl
            };
        }
        
        if (!displayNameLink || !displayNameLink.href) {
            return;
        }

        const url = new URL(displayNameLink.href);
        const isNewAttachment = url.searchParams.get('isNew') === 'true';

        const displayName = displayNameLink.textContent;
        const fileName = url.searchParams.get('fileName');

        if (coreUI?.showConfirmDialog) {
            coreUI.showConfirmDialog({
                contentHtml: this.#locales?.confirmRemoveAttachment || 'Remove attachment?',
                callbackOk: async () => {
                    if (isNewAttachment) {
                        this.#removeAttachmentFromUI(container, displayName, false);
                    }
                    else {
                        const success = await this.#deleteAttachmentOnServer(fileName);

                        if (success) {
                            this.#removeAttachmentFromUI(container, displayName, false);
                        }
                    }
                }
            });
        }
    }

    /**
     * Handles draw button click events.
     * @param {MouseEvent} event The click event.
     * @returns {void}
     * 
     * @private
     * @memberof PreviewContainer
     */
    onDrawClick(event) {
        const container = event.target.closest('.preview-card');
        const imageElement = container.querySelector('.preview-thumb');
        let displayNameLink = container.querySelector('.display-name');

        // Display-name element might not exist.
        if (!displayNameLink) {
            displayNameLink = {
                textContent: container.dataset.displayName,
                href: container.dataset.attachmentUrl
            };
        }

        if (!imageElement || !displayNameLink) {
            return;
        }

        // Initialize drawing handler if not already created.
        if (!this.#drawingHandler) {
            this.#initializeDrawingHandler();
        }

        if (this.#drawingHandler) {
            this.#drawingHandler.open(container, imageElement, this.drawingLineThickness);
        }
    }

    /**
     * Handles download button click events.
     * @param {MouseEvent} event The click event.
     * @returns {void}
     * 
     * @private
     * @memberof PreviewContainer
     */
    onDownloadClick(event) {
        const container = event.target.closest('.preview-card');
        let displayNameLink = container.querySelector('.display-name');
        
        // Display-name element might not exist.
        if (!displayNameLink) {
            displayNameLink = {
                textContent: container.dataset.displayName,
                href: container.dataset.attachmentUrl
            };
        }
        
        if (!displayNameLink || !displayNameLink.href) {
            return;
        }

        // Create temporary link and trigger download.
        const downloadLink = document.createElement('a');
        downloadLink.href = displayNameLink.href;
        downloadLink.download = displayNameLink.textContent || 'download';
        downloadLink.style.display = 'none';
        
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    /**
     * Handles click events on preview cards when in stacked mode, delegating to the preview functionality.
     * This method is specifically designed for stacked mode where cards are displayed as an overlapping deck 
     * rather than in a grid layout. In stacked mode, the entire card becomes clickable for preview functionality,
     * eliminating the need for a separate preview button to optimize space and improve touch interaction.
     * @param {MouseEvent} event The click event triggered on the stacked card element.
     * @returns {void} No return value.
     * 
     * @private
     * @memberof PreviewContainer
     */
    onStackedCardClick(event) {
        // Prevent triggering if a button was clicked.
        if (event.target.closest('button')) {
            return;
        }

        // Trigger the preview functionality.
        this.onPreviewClick(event);
    }


    /**
     * Handles preview button click events.
     * @param {MouseEvent} event The click event.
     * @returns {void}
     * 
     * @private
     * @memberof PreviewContainer
     */
    onPreviewClick(event) {
        const button = event.target.closest('.button');
        const attachmentContainer = button ? button.closest('.preview-card') : event.currentTarget;
        const attachmentsContainer = attachmentContainer.parentElement;

        // Get display name from either visible element or stored data attribute.
        let displayName = attachmentContainer.querySelector('.display-name');

        if (!displayName) {
            // In stacked mode, create a temporary element for compatibility.
            displayName = document.createElement('a');
            displayName.textContent = attachmentContainer.dataset.displayName;
            displayName.href = attachmentContainer.dataset.attachmentUrl;
        }

        // Find current index in the collection of previewable attachments.
        const previewableAttachments = [...attachmentsContainer.querySelectorAll('.preview-card')].filter((element) => {
            const displayNameElement = element.querySelector('.display-name');

            let fileName;
            
            if (displayNameElement) {
                fileName = displayNameElement.textContent;
            }
            else {
                fileName = element.dataset.displayName;
            }

            return coreUI.isPreviewable(fileName);
        });

        // Where are we?
        let currentIndex = previewableAttachments.findIndex((container) => {
            return container === attachmentContainer;
        });

        // Create dialog-window element.
        const dialogElement = DialogWindow.create({
            clampToViewport: false,
            height: coreUI.getPopupHeight(PreviewContainer.MIN_PREVIEW_HEIGHT),
            modal: true,
            owner: 'parent',
            title: this.#locales?.preview || 'Preview',
            width: coreUI.getPopupWidth(PreviewContainer.MIN_PREVIEW_WIDTH)
        });
        
        // Add class for CSS targeting.
        dialogElement.classList.add('viewer-dialog-window');

        // Get dialog document for element creation.
        const dialogDocument = dialogElement.ownerDocument;

        // Create content container in the dialog's document context.
        const contentContainer = dialogDocument.createElement('div');
        contentContainer.className = 'viewer-content';
        contentContainer.tabIndex = -1;

        // Create navigation if we have multiple attachments.
        let indexDiv = null;
        let navigationDiv = null;
        let nextButton = null;
        let prevButton = null;

        if (previewableAttachments.length > 1) {
            // Create navigation container.
            navigationDiv = dialogDocument.createElement('div');
            navigationDiv.className = 'viewer-nav';

            // Create previous button.
            prevButton = dialogDocument.createElement('button');
            prevButton.type = 'button';
            prevButton.className = 'nav-button nav-prev';
            prevButton.title = this.#locales?.previous || 'Vorige';
            prevButton.innerHTML = '<span class="nav-arrow lf-icon-arrow-back-ios"></span>';

            // Create next button.
            nextButton = dialogDocument.createElement('button');
            nextButton.type = 'button';
            nextButton.className = 'nav-button nav-next';
            nextButton.title = this.#locales?.next || 'Volgende';
            nextButton.innerHTML = '<span class="nav-arrow lf-icon-arrow-forward-ios"></span>';

            navigationDiv.appendChild(prevButton);
            navigationDiv.appendChild(nextButton);

            // Create attachment index display.
            indexDiv = dialogDocument.createElement('div');
            indexDiv.className = 'viewer-index';
        }

        /**
         * Load attachment content into the dialog.
         * @param {Number} index The index of the attachment to load.
         */
        const loadAttachmentContent = (index) => {
            if (index < 0 || index >= previewableAttachments.length) {
                return;
            }

            // Remove show class for animation.
            if (indexDiv) {
                indexDiv.classList.remove('visible');
            }

            currentIndex = index;
            const container = previewableAttachments[index];
            let fileName = container.querySelector('.display-name');
            if (!fileName) {
                // Create temporary element for stacked mode compatibility.
                fileName = document.createElement('a');
                fileName.textContent = container.dataset.displayName;
                fileName.href = container.dataset.attachmentUrl;
            }

            // Clear previous content.
            contentContainer.innerHTML = '';

            // Update dialog content based on file type.
            if (coreUI.isPdf(fileName.textContent)) {
                const iframe = dialogDocument.createElement('iframe');

                iframe.src = baseUrl + '/Dialogs/PdfViewer?pdf=' + encodeURIComponent(fileName.href);

                contentContainer.appendChild(iframe);
            }
            else if (coreUI.isImage(fileName.textContent)) {
                const imageElement = container.querySelector('.preview-thumb');
                const img = dialogDocument.createElement('img');

                img.className = 'viewer-image';
                img.src = imageElement.src;

                contentContainer.appendChild(img);
            }

            // Update the title of the dialog.
            dialogElement.title = (this.#locales?.preview || 'Preview') + ': ' + fileName.textContent;

            // Only show navigation when we have more than one attachment to display.
            if (previewableAttachments.length > 1) {
                // Update counter.
                const indexSpan = dialogDocument.createElement('span');

                indexSpan.className = 'index';
                indexSpan.innerHTML = `${currentIndex + 1}&nbsp;/&nbsp;${previewableAttachments.length}`;
                indexDiv.innerHTML = '';
                indexDiv.appendChild(indexSpan);

                // Update navigation visibility.
                if (prevButton) {
                    prevButton.style.visibility = currentIndex > 0 ? 'visible' : 'hidden';
                }

                if (nextButton) {
                    nextButton.style.visibility = currentIndex < previewableAttachments.length - 1 ? 'visible' : 'hidden';
                }

                // Force reflow and show.
                void indexDiv.offsetWidth;
                indexDiv.classList.add('visible');
            }
        };

        // Attach navigation events.
        if (prevButton) {
            prevButton.addEventListener('click', () => {
                if (currentIndex > 0) {
                    loadAttachmentContent(currentIndex - 1);
                }
            });
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                if (currentIndex < previewableAttachments.length - 1) {
                    loadAttachmentContent(currentIndex + 1);
                }
            });
        }

        // Add keyboard navigation.
        const onKeydownHandler = (event) => {
            if (event.key === 'ArrowLeft' && currentIndex > 0 && prevButton) {
                loadAttachmentContent(currentIndex - 1);
            }
            else if (event.key === 'ArrowRight' && currentIndex < previewableAttachments.length - 1 && nextButton) {
                loadAttachmentContent(currentIndex + 1);
            }
            else if (event.key === 'Escape') {
                dialogElement.close();
            }
        };

        // Add keyboard handler.
        dialogDocument.addEventListener('keydown', onKeydownHandler);
        
        // Add swipe gesture support for mobile navigation.
        if (previewableAttachments.length > 1) {
            let touchStartX = 0;
            let touchStartY = 0;
            let touchEndX = 0;
            let touchEndY = 0;
            
            const onHandleSwipe = () => {
                const swipeThreshold = 50;
                const verticalThreshold = 100;
                const deltaX = touchEndX - touchStartX;
                const deltaY = Math.abs(touchEndY - touchStartY);
                
                // Only process horizontal swipes (ignore vertical scrolling).
                if (Math.abs(deltaX) > swipeThreshold && deltaY < verticalThreshold) {
                    if (deltaX > 0 && currentIndex > 0) {
                        // Swipe right - go to previous.
                        loadAttachmentContent(currentIndex - 1);
                    }
                    else if (deltaX < 0 && currentIndex < previewableAttachments.length - 1) {
                        // Swipe left - go to next.
                        loadAttachmentContent(currentIndex + 1);
                    }
                }
            };
            
            contentContainer.addEventListener('touchstart', (event) => {
                touchStartX = event.changedTouches[0].screenX;
                touchStartY = event.changedTouches[0].screenY;
            }, { passive: true });
            
            contentContainer.addEventListener('touchend', (event) => {
                touchEndX = event.changedTouches[0].screenX;
                touchEndY = event.changedTouches[0].screenY;
                onHandleSwipe();
            }, { passive: true });
        }

        // Handle dialog close to clean up keyboard listener.
        dialogElement.addEventListener('dialogclose', () => {
            document.removeEventListener('keydown', onKeydownHandler);

            // Remove dialog element from DOM.
            if (dialogElement.parentNode) {
                dialogElement.parentNode.removeChild(dialogElement);
            }
        });

        // Build the dialog content structure.
        const dialogContent = dialogDocument.createElement('div');
        dialogContent.classList.add('viewer-dialog');

        // Add content container.
        dialogContent.appendChild(contentContainer);

        // Add navigation if present.
        if (navigationDiv) {
            // Make buttons interactive.
            if (prevButton) {
                prevButton.style.pointerEvents = 'auto';
            }

            if (nextButton) {
                nextButton.style.pointerEvents = 'auto';
            }

            dialogContent.appendChild(navigationDiv);
        }

        // Add index display if present.
        if (indexDiv) {
            dialogContent.appendChild(indexDiv);
        }

        // Initialize content before opening dialog.
        loadAttachmentContent(currentIndex);

        // Open the dialog.
        dialogElement.open(dialogContent, {
            title: (this.#locales?.preview || 'Preview') + ': ' + displayName.textContent
        });

        // Focus content container to enable immediate keyboard navigation.
        contentContainer.focus();

        // Fix display on mobile.
        if (coreUI.isMobile()) {
            const dialogContainer = dialogElement.querySelector('.dialog-container');

            if (dialogContainer) {
                Object.assign(dialogContainer.style, {
                    position: 'fixed',
                    insetBlockStart: '0',
                    insetInlineStart: '0',
                    top: '0',
                    left: '0',
                    transform: 'none',
                    width: '100vw',
                    height: '100dvh',
                    maxWidth: '100vw',
                    maxHeight: '100dvh'
                });
            }
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Adds an attachment to the container.
     * @param {Object} file File object containing guid, displayName, and url.
     * @returns {void}
     * 
     * @memberof PreviewContainer
     */
    addAttachment(file) {
        // Check if an attachment with the same GUID already exists (temporary file being claimed).
        if (file.guid && file.fileName) {
            const existingElement = this.#guidToElementMap.get(file.guid);
            
            if (existingElement) {
                // Update the href to use the claimed fileName instead of the temporary GUID.
                const newUrl = `${baseUrl}/api/attachments?fileName=${encodeURIComponent(file.fileName)}&displayName=${encodeURIComponent(file.displayName)}${this.#inspectionGuid ? `&inspectionGuid=${this.#inspectionGuid}` : ''}`;
                
                // Update display-name link if it exists.
                const displayNameLink = existingElement.querySelector('.display-name');
                if (displayNameLink) {
                    displayNameLink.href = newUrl;
                    displayNameLink.textContent = file.displayName;
                    displayNameLink.download = file.displayName;
                }
                
                // Update dataset for stacked mode.
                if (existingElement.dataset.attachmentUrl) {
                    existingElement.dataset.attachmentUrl = newUrl;
                    existingElement.dataset.displayName = file.displayName;
                }
                
                // Update image source if it's an image preview.
                const imgElement = existingElement.querySelector('.preview-thumb');
                if (imgElement) {
                    imgElement.src = newUrl;
                }
                
                // Remove old GUID from map since it's now claimed with fileName.
                this.#guidToElementMap.delete(file.guid);
                
                // Dispatch attachment updated event.
                this.dispatchEvent(new CustomEvent('attachment-updated', {
                    bubbles: true,
                    detail: {
                        displayName: file.displayName,
                        fileName: file.fileName,
                        guid: file.guid
                    }
                }));
                
                return;
            }
        }
        
        // No existing attachment found, add as new.
        const attachmentElement = this.#createAttachmentElement(file);

        this.#attachmentContainer.appendChild(attachmentElement);
        
        // Register if it has a GUID (temporary file).
        if (file.guid && !file.fileName) {
            this.#guidToElementMap.set(file.guid, attachmentElement);
        }

        // Update scroll width for stacked view.
        if (this.stacked) {
            this.#updateScrollWidth();
        }

        // Dispatch attachment added event.
        this.dispatchEvent(new CustomEvent('attachment-added', {
            bubbles: true,
            detail: {
                displayName: file.displayName,
                fileName: file.fileName,
                guid: file.guid
            }
        }));
    }

    /**
     * Removes all attachments from the container.
     * @returns {void}
     * 
     * @memberof PreviewContainer
     */
    clearAttachments() {
        if (this.#attachmentContainer) {
            this.#attachmentContainer.innerHTML = '';
        }
        
        // Clear the GUID map.
        this.#guidToElementMap.clear();
    }

    /**
     * Gets all attachment data objects from the container.
     * @returns {Array} Array of attachment data objects.
     * @memberof PreviewContainer
     */
    getAttachmentData() {
        const attachmentElements = this.getAttachments();
        const attachments = [];

        attachmentElements.forEach((element) => {
            const displayNameLink = element.querySelector('.display-name');

            if (displayNameLink && displayNameLink.href) {
                const url = new URL(displayNameLink.href);
                const displayName = displayNameLink.textContent;
                const fileName = url.searchParams.get('fileName');
                const guid = url.searchParams.get('guid');
                const state = url.searchParams.get('isNew') ? 'new' : 'saved';

                attachments.push({
                    displayName: displayName,
                    fileName: fileName,
                    guid: guid,
                    state: state
                });
            }
        });

        return attachments;
    }

    /**
     * Gets all attachment elements in the container.
     * @returns {HTMLElement[]} List of attachment elements.
     * 
     * @memberof PreviewContainer
     */
    getAttachments() {
        return this.#attachmentContainer ? [...this.#attachmentContainer.querySelectorAll('.preview-card')] : [];
    }

    /**
     * Sets the attachments in the container, clearing existing ones.
     * @param {Array} attachments Array of attachment objects.
     * @returns {void}
     * 
     * @memberof PreviewContainer
     */
    setAttachments(attachments) {
        this.clearAttachments();
        
        if (Array.isArray(attachments)) {
            attachments.forEach((attachment) => {
                this.addAttachment(attachment);
            });
        }
    }

    /**
     * Sets the inspection guid.
     * @param {string} inspectionGuid The inspection guid.
     * @returns {void}
     * 
     * @memberof PreviewContainer
     */
    setInspectionGuid(inspectionGuid) {
        this.#inspectionGuid = inspectionGuid;
    }

    /**
     * Validates the component state.
     * @returns {boolean} True if valid, false otherwise.
     * 
     * @memberof PreviewContainer
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Creates an attachment element.
     * @param {Object} file File object containing guid, displayName, and url.
     * @returns {HTMLElement} The created attachment element.
     * @private
     * @memberof PreviewContainer
     */
    #createAttachmentElement(file) {
        const container = document.createElement('div');
        container.className = 'preview-card';

        const attachmentItem = document.createElement('div');
        attachmentItem.className = 'preview-media';

        const buttons = document.createElement('div');
        buttons.className = 'preview-controls';

        let url;

        if (file.guid && !file.fileName) {
            url = `${baseUrl}/api/attachments?isNew=true&guid=${encodeURIComponent(file.guid)}&displayName=${encodeURIComponent(file.displayName)}`;
        }
        else {
            url = `${baseUrl}/api/attachments?fileName=${encodeURIComponent(file.fileName)}&displayName=${encodeURIComponent(file.displayName)}`;
            
            if (this.#inspectionGuid) {
                url += `&inspectionGuid=${this.#inspectionGuid}`;
            }
        }

        const displayNameLink = document.createElement('a');
        displayNameLink.classList.add('preview-label', 'display-name');
        displayNameLink.href = url;
        displayNameLink.textContent = file.displayName;
        displayNameLink.download = file.displayName;

        if (coreUI?.isImage(file.displayName)) {
            const img = document.createElement('img');
            img.className = 'preview-thumb';
            img.src = displayNameLink.href;

            attachmentItem.appendChild(img);
        }
        else if (coreUI?.isPdf(file.displayName)) {
            const pdfIcon = document.createElement('span');
            pdfIcon.className = 'file-type-icon lf-icon-file-pdf icon-large';

            attachmentItem.appendChild(pdfIcon);
        }
        else {
            const fileIcon = document.createElement('span');
            fileIcon.className = `file-type-icon lf-icon-file-${core?.getFileIconSuffix(file.displayName) || 'generic'} icon-large`;

            attachmentItem.appendChild(fileIcon);
        }

        // Can we preview the file?
        // In stacked mode, the entire card is clickable for preview, so no button needed.
        if (coreUI.isPreviewable(file.displayName) && !this.stacked) {
            const previewButton = this.#createIconButton('preview-attachment', 'lf-icon-zoom-in', this.#locales?.preview || 'Voorbeeld');

            buttons.appendChild(previewButton);

            previewButton.addEventListener('click', (event) => {
                this.onPreviewClick(event);
            }, { capture: false });
        }

        // Add download button (only in stacked mode where display names are not visible).
        if (this.stacked) {
            const downloadButton = this.#createIconButton('download-attachment', 'lf-icon-download', this.#locales?.download || 'Downloaden');

            buttons.appendChild(downloadButton);

            downloadButton.addEventListener('click', (event) => {
                this.onDownloadClick(event);
            }, { capture: false });
        }

        // Only add other buttons when not in read-only mode.
        if (!this.readOnly) {

            // Add draw button for images.
            if (coreUI?.isImage(file.displayName)) {
                const drawButton = this.#createIconButton('draw-attachment', 'lf-icon-brush', this.#locales?.draw || 'Tekenen');

                buttons.appendChild(drawButton);

                drawButton.addEventListener('click', (event) => {
                    this.onDrawClick(event);
                }, { capture: false });
            }

            // Add delete button.
            const deleteButton = this.#createIconButton('delete-attachment', 'lf-icon-delete', this.#locales?.delete || 'Verwijderen');

            buttons.appendChild(deleteButton);
            
            deleteButton.addEventListener('click', (event) => {
                this.onDeleteClick(event);
            }, { capture: false });
        }

        attachmentItem.appendChild(buttons);
        container.appendChild(attachmentItem);
        
        // Only add display name link if not in stacked mode.
        if (!this.stacked) {
            container.appendChild(displayNameLink);
        }

        // Handle stacked mode.
        if (this.stacked) {
            // Store display name and URL as data attributes for preview functionality.
            container.dataset.displayName = file.displayName;
            container.dataset.attachmentUrl = url;
            
            // Add click handler for preview if file is previewable.
            if (coreUI.isPreviewable(file.displayName)) {
                // Add class to indicate this card is previewable.
                container.classList.add('previewable');

                container.addEventListener('click', (event) => {
                    this.onStackedCardClick(event);
                }, { capture: false });
            }
            else {
                // For non-previewable files, add hover-persistent class.
                container.classList.add('non-previewable');
            }
            
            // Add mobile touch handling.
            this.#setupMobileTouchHandling(container);
        }

        return container;
    }

    /**
     * Creates the drawing handler object with all drawing functionality.
     * @param {HTMLElement} drawingContainer The drawing container element.
     * @param {Document} topDocument The top-level document object.
     * @returns {Object} The drawing handler object.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #createDrawingHandler(drawingContainer, topDocument = document) {

        // Add event listeners for drawing.
        function addDrawingListeners() {
            // Mouse events.
            canvasElement.addEventListener('mousedown', startDrawing);
            canvasElement.addEventListener('mousemove', draw);
            canvasElement.addEventListener('mouseup', stopDrawing);
            canvasElement.addEventListener('mouseout', stopDrawing);

            // Touch events.
            canvasElement.addEventListener('touchstart', startDrawing, { passive: false });
            canvasElement.addEventListener('touchmove', draw, { passive: false });
            canvasElement.addEventListener('touchend', stopDrawing, { passive: false });

            // Prevent pull-to-refresh and other touch gestures.
            topDocument.addEventListener('touchstart', preventRefresh, { passive: false });
            topDocument.addEventListener('touchmove', preventRefresh, { passive: false });
            topDocument.addEventListener('touchend', preventRefresh, { passive: false });
        }

        // Local methods.
        /**
         * Closes the drawing interface without saving.
         */
        function closeDrawing() {
            removeDrawingListeners();
            
            // Close the parent dialog.
            const parentDialog = drawingContainer.parentElement;

            if (parentDialog && parentDialog.tagName === 'DIALOG') {
                parentDialog.close();
            }

            htmlElements.forEach((el) => {
                el.classList.remove('drawing-mode');
            });

            toggleZoomPrevention(false);

            window.removeEventListener('resize', resizeCanvas, { passive: true });
        }

        function draw(event) {
            if (!drawing) {
                return;
            }

            // Prevent default behavior for touch events to avoid pull-to-refresh.
            if (event.type === 'touchmove') {
                event.preventDefault();
                event.stopPropagation();
            }

            const rect = canvasElement.getBoundingClientRect();
            const scaleX = canvasElement.width / rect.width;
            const scaleY = canvasElement.height / rect.height;

            let currentX, currentY;

            if (event.type === 'touchmove') {
                const touch = event.touches[0];

                currentX = (touch.clientX - rect.left) * scaleX;
                currentY = (touch.clientY - rect.top) * scaleY;
            }
            else {
                currentX = (event.clientX - rect.left) * scaleX;
                currentY = (event.clientY - rect.top) * scaleY;
            }

            // Add point to current path.
            const currentPath = paths[paths.length - 1];
            currentPath.points.push({ x: currentX, y: currentY });

            // Draw line segment.
            ctx.strokeStyle = currentColor;
            ctx.lineWidth = currentLineThickness;
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            ctx.lineTo(currentX, currentY);
            ctx.stroke();

            lastX = currentX;
            lastY = currentY;
        }

        /**
         * Opens the drawing interface.
         * @param {HTMLElement} container The attachment container.
         * @param {HTMLImageElement} imageElement The image element.
         * @param {number} lineThickness The drawing line thickness.
         */
        function open(container, imageElement, lineThickness) {
            // Clear previous state.
            paths = [];
            currentLineThickness = lineThickness || DEFAULT_LINE_THICKNESS;

            window.addEventListener('resize', resizeCanvas, { passive: true });
            
            // Get the parent dialog element.
            const parentDialog = drawingContainer.parentElement;
            
            // Show the native dialog as modal.
            if (parentDialog && parentDialog.tagName === 'DIALOG') {
                parentDialog.showModal();
            }

            // Need to do this first to get correct dimensions.
            drawingContainer.style.visibility = 'hidden';

            htmlElements.forEach((el) => {
                el.classList.add('drawing-mode');
            });

            toggleZoomPrevention(true);

            attachmentContainer = container;

            const width = imageElement.naturalWidth;
            const height = imageElement.naturalHeight;

            // Set canvas dimensions.
            canvasElement.width = width;
            canvasElement.height = height;

            // Create background image.
            backgroundImage = new Image();
            backgroundImage.onload = () => {
                // Draw background image.
                ctx.drawImage(backgroundImage, 0, 0, width, height);

                // Setup size helper for responsive scaling.
                sizeHelper.src = imageElement.src;

                setTimeout(() => {
                    resizeCanvas();
                    drawingContainer.style.visibility = 'visible';

                    // Add drawing listeners after everything is setup.
                    addDrawingListeners();
                }, 100);
            };

            backgroundImage.src = imageElement.src;
            setColor(defaultColorButton);
        }

        // Prevent pull-to-refresh and other touch gestures during drawing.
        function preventRefresh(event) {
            // Only prevent if we're in drawing mode and not interacting with UI elements.
            if (topDocument.documentElement.classList.contains('drawing-mode')) {
                // Allow clicks on buttons and UI elements.
                const target = event.target;
                const isUIElement = target.closest('.drawing-tools') || 
                                  target.closest('.color-picker') ||
                                  target.closest('.tool-button');
                
                if (!isUIElement) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        }

        // Clear canvas and redraw background + paths.
        function redrawCanvas() {
            ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

            // Draw background image.
            if (backgroundImage) {
                ctx.drawImage(backgroundImage, 0, 0, canvasElement.width, canvasElement.height);
            }

            // Redraw all paths.
            paths.forEach((path) => {
                if (path.points.length < 2) {
                    return;
                }

                ctx.strokeStyle = path.color;
                ctx.lineWidth = path.lineWidth;
                ctx.beginPath();
                ctx.moveTo(path.points[0].x, path.points[0].y);

                for (let i = 1; i < path.points.length; i++) {
                    ctx.lineTo(path.points[i].x, path.points[i].y);
                }

                ctx.stroke();
            });
        }

        // Remove event listeners for drawing.
        function removeDrawingListeners() {
            canvasElement.removeEventListener('mousedown', startDrawing);
            canvasElement.removeEventListener('mousemove', draw);
            canvasElement.removeEventListener('mouseup', stopDrawing);
            canvasElement.removeEventListener('mouseout', stopDrawing);
            canvasElement.removeEventListener('touchstart', startDrawing);
            canvasElement.removeEventListener('touchmove', draw);
            canvasElement.removeEventListener('touchend', stopDrawing);

            // Remove global touch prevention.
            topDocument.removeEventListener('touchstart', preventRefresh);
            topDocument.removeEventListener('touchmove', preventRefresh);
            topDocument.removeEventListener('touchend', preventRefresh);
        }

        /**
         * Resizes the canvas to fit the container.
         */
        function resizeCanvas() {
            const containerWidth = sizeHelper.offsetWidth;
            const containerHeight = sizeHelper.offsetHeight;

            // Set canvas display size.
            canvasElement.style.width = containerWidth + 'px';
            canvasElement.style.height = containerHeight + 'px';

            // Redraw canvas content at new scale.
            redrawCanvas();
        }

        /**
         * Closes the drawing interface and saves the changes.
         */
        async function saveDrawing() {
            removeDrawingListeners();
            
            // Close the parent dialog.
            const parentDialog = drawingContainer.parentElement;
            if (parentDialog && parentDialog.tagName === 'DIALOG') {
                parentDialog.close();
            }

            htmlElements.forEach((el) => {
                el.classList.remove('drawing-mode');
            });

            toggleZoomPrevention(false);
            window.removeEventListener('resize', resizeCanvas, { passive: true });

            // No need to save when no drawing is present.
            const hasDrawnPaths = paths.length > 0;

            if (!hasDrawnPaths) {
                return;
            }

            // Convert canvas to data URI and update image.
            const JPEG_QUALITY = 0.9;
            const dataUri = canvasElement.toDataURL('image/jpeg', JPEG_QUALITY);
            const imageElement = attachmentContainer.querySelector('.preview-thumb');

            if (imageElement) {
                imageElement.src = dataUri;
            }

            const displayNameLink = attachmentContainer.querySelector('.display-name');
            const originalDisplayName = displayNameLink.textContent;

            // Create JPEG filename by replacing the original extension with .jpg.
            const extensionIndex = originalDisplayName.lastIndexOf('.');
            const baseFileName = extensionIndex > 0 ? originalDisplayName.substring(0, extensionIndex) : originalDisplayName;
            const jpegDisplayName = baseFileName + '.jpg';

            // Save image.
            const imageBlob = await fetch(dataUri).then((res) => {
                return res.blob();
            });

            const file = new File([imageBlob], jpegDisplayName, { type: 'image/jpeg' });

            // Upload drawn image to server.
            await uploadDrawnImage(file, attachmentContainer);
        }

        /**
         * Uploads the drawn image to the server and updates the attachment container.
         * @param {File} file The drawn image file to upload.
         * @param {HTMLElement} container The attachment container element.
         */
        const uploadDrawnImage = async (file, container) => {
            // Get current attachment data to determine replacement scenario.
            const displayNameLink = container.querySelector('.display-name');
            const attachmentUrl = new URL(displayNameLink.href);
            const currentGuid = attachmentUrl.searchParams.get('guid');
            const fileName = attachmentUrl.searchParams.get('fileName');
            const inspectionId = attachmentUrl.searchParams.get('inspectionId') || '-1';
            const isNew = attachmentUrl.searchParams.get('isNew') === 'true';

            // Prepare form data for upload.
            const formData = new FormData();
            formData.append('attachments', file);

            // Determine if we're replacing a claimed attachment or a temp file.
            if (!isNew && fileName) {
                // This is a claimed attachment - pass originalFileName for replacement.
                formData.append('originalFileName', fileName);
            }
            else if (currentGuid) {
                // This is a temp file - pass previousGuid for cleanup.
                formData.append('previousGuid', currentGuid);
            }

            // Determine API endpoint.
            const apiPath = this.#getApiPath();
            const uploadUrl = apiPath || `/api/inspections/${inspectionId}/attachments`;

            // Upload to server.
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status}`);
            }

            const result = await response.json();

            if (result.errors && result.errors.length > 0) {
                throw new Error(result.errors[0].error);
            }

            // Update attachment container with new data.
            if (result.result && result.result.length > 0) {
                const newAttachment = result.result[0];

                updateAttachmentContainer(container, newAttachment);
                    
                // Notify parent window of attachment update.
                notifyAttachmentUpdate(newAttachment, isNew);
            }
        };

        /**
         * Updates the attachment container with new attachment data.
         * @param {HTMLElement} container The attachment container element.
         * @param {Object} attachmentData The new attachment data from server.
         */
        function updateAttachmentContainer(container, attachmentData) {
            const displayNameLink = container.querySelector('.display-name');
            
            if (displayNameLink && attachmentData.displayName) {
                const baseUrl = window.location.protocol + '//' + window.location.host;
                const encodedDisplayName = encodeURIComponent(attachmentData.displayName);

                // Check if this is a replacement of a claimed attachment or a new temp attachment.
                if (attachmentData.fileName && !attachmentData.guid) {
                    // This is a replaced claimed attachment - use fileName format.
                    const encodedFileName = encodeURIComponent(attachmentData.fileName);
                    const claimedUrl = `${baseUrl}/api/attachments?fileName=${encodedFileName}&displayName=${encodedDisplayName}`;
                    displayNameLink.href = claimedUrl;
                }
                else if (attachmentData.guid) {
                    // This could be a new temp attachment or an updated temp attachment.
                    const guidWithExtension = attachmentData.guid;
                    const extension = guidWithExtension.includes('.') ? '.' + guidWithExtension.split('.').pop() : '';
                    const guidOnly = guidWithExtension.replace(extension, '');
                    const encodedGuid = encodeURIComponent(guidOnly);
                    const claimableUrl = `${baseUrl}/api/attachments?isNew=true&guid=${encodedGuid}&displayName=${encodedDisplayName}`;
                    displayNameLink.href = claimableUrl;
                }
                else if (attachmentData.url) {
                    // Fallback to using the provided URL directly.
                    displayNameLink.href = attachmentData.url;
                }
                
                // Update display name.
                displayNameLink.textContent = attachmentData.displayName;
            }
        }

        /**
         * Notifies the parent window about attachment updates.
         * @param {Object} attachmentData The updated attachment data.
         * @param {boolean} wasNew Whether the original attachment was new.
         */
        function notifyAttachmentUpdate(attachmentData, wasNew) {
            // Send message to parent window for inspection card updates.
            const message = {
                type: 'attachment-drawn-updated',
                data: {
                    attachmentData: attachmentData,
                    wasNew: wasNew
                }
            };

            // Use window.origin for security instead of '*'.
            if (window.parent !== window) {
                window.parent.postMessage(message, window.origin);
            }
        }

        /**
         * Sets the drawing color.
         * @param {HTMLElement} colorButton The color button element.
         */
        function setColor(colorButton) {
            colorButtons.forEach((btn) => {
                btn.classList.remove('selected');
            });
            colorButton.classList.add('selected');

            const color = window.getComputedStyle(colorButton).backgroundColor;
            currentColor = color;
        }

        // Drawing event handlers.
        function startDrawing(event) {
            // Prevent default behavior for touch events to avoid pull-to-refresh.
            if (event.type === 'touchstart') {
                event.preventDefault();
                event.stopPropagation();
            }

            drawing = true;

            const rect = canvasElement.getBoundingClientRect();
            const scaleX = canvasElement.width / rect.width;
            const scaleY = canvasElement.height / rect.height;

            if (event.type === 'touchstart') {
                const touch = event.touches[0];
                lastX = (touch.clientX - rect.left) * scaleX;
                lastY = (touch.clientY - rect.top) * scaleY;
            }
            else {
                lastX = (event.clientX - rect.left) * scaleX;
                lastY = (event.clientY - rect.top) * scaleY;
            }

            // Start new path.
            const newPath = {
                color: currentColor,
                lineWidth: currentLineThickness,
                points: [{ x: lastX, y: lastY }]
            };

            paths.push(newPath);
        }

        function stopDrawing(event) {
            // Prevent default behavior for touch events to avoid pull-to-refresh.
            if (event && event.type === 'touchend') {
                event.preventDefault();
                event.stopPropagation();
            }

            drawing = false;
        }

        /**
         * Toggles zoom prevention for mobile devices.
         * @param {boolean} state Whether to prevent zoom.
         */
        function toggleZoomPrevention(state) {
            const metaViewport = topDocument.querySelector('meta[name=viewport]');

            if (!metaViewport) {
                return;
            }

            const preventScale = ', maximum-scale=1';
            const hasPreventScale = metaViewport.content.includes(preventScale);

            if (state && hasPreventScale) {
                // Is iPhone, where zoom should always be prevented due to automatic zooming.
                return;
            }

            if (state) {
                metaViewport.content += preventScale;
            }
            else if (!state) {
                metaViewport.content = metaViewport.content.replace(preventScale, '');
            }
        }

        // Elements.
        const canvasElement = drawingContainer.querySelector('.drawing-canvas');
        const sizeHelper = drawingContainer.querySelector('.canvas-sizer');
        const undoButton = drawingContainer.querySelector('.undo-tool');
        const closeButton = drawingContainer.querySelector('.close-tool');
        const saveButton = drawingContainer.querySelector('.save-tool');
        const colorButtons = drawingContainer.querySelectorAll('.color-picker');
        const defaultColorButton = colorButtons[0];
        const htmlElements = [topDocument.documentElement];

        if (window.top && window.top !== window) {
            htmlElements.push(window.top.document.documentElement);
        }

        const DEFAULT_LINE_THICKNESS = 3;

        let attachmentContainer = null;
        let backgroundImage = null;
        let currentColor = '#3299da';
        let currentLineThickness = DEFAULT_LINE_THICKNESS;
        let drawing = false;
        let lastX = 0;
        let lastY = 0;
        let paths = [];

        // Get canvas context.
        const ctx = canvasElement.getContext('2d');

        // Set canvas drawing properties.
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Event handlers.
        closeButton.addEventListener('click', (event) => {
            event.stopPropagation();
            closeDrawing();
        });
        saveButton.addEventListener('click', (event) => {
            event.stopPropagation();
            saveDrawing.call(this);
        });

        colorButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                setColor(button);
            });
        });

        undoButton.addEventListener('click', (event) => {
            event.stopPropagation();
            if (paths.length > 0) {
                paths.pop();
                redrawCanvas();
            }
        });

        return {
            open: open
        };
    }

    /**
     * Creates an icon button element.
     * @param {string} buttonClass CSS class for the button.
     * @param {string} iconClass CSS class for the icon.
     * @param {string} title Title attribute for the button.
     * @returns {HTMLButtonElement} The created button element.
     * @private
     * @memberof PreviewContainer
     */
    #createIconButton(buttonClass, iconClass, title) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `button button-icon-only ${buttonClass}`;
        button.title = title;

        const iconSpan = document.createElement('span');
        iconSpan.className = `button-icon ${iconClass}`;
        button.appendChild(iconSpan);

        return button;
    }

    /**
     * Handles server deletion of an attachment.
     * @param {string} fileName The file name to delete.
     * @returns {Promise<boolean>} True if deletion succeeded, false otherwise.
     * 
     * @private
     * @memberof PreviewContainer
     */
    async #deleteAttachmentOnServer(fileName) {
        const formData = new FormData();

        formData.append('fileName', fileName);

        return await window.core.fetch({
            config: {
                body: formData,
                method: 'DELETE'
            },
            url: this.#getApiPath()
        })
        .catch(leanformsNext.markNetworkError)
        .then((response) => {
            if (response.ok) {
                return true;
            }
            else {
                leanformsNext.onAjaxError(response);

                return false;
            }
        });
    }

    /**
     * Finds the connected file-upload component that references this preview container.
     * @returns {HTMLElement|null} The connected file-upload component or null if not found.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #findConnectedFileUpload() {
        if (!this.id) {
            return null;
        }

        // Find file-upload component that references this preview container by ID.
        return document.querySelector(`file-upload[preview-container-id="${this.id}"]`);
    }

    /**
     * Gets the effective (delete) API path, trying multiple sources in order.
     * @returns {string|null} The (delete) API path to use.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #getApiPath() {
        // First try explicit delete-api-path attribute.
        if (this.apiPath) {
            return this.apiPath;
        }

        // Try to derive from connected file-upload component.
        const fileUpload = this.#findConnectedFileUpload();

        if (fileUpload && fileUpload.apiPath) {
            // This assumes the delete endpoint is the same as the upload endpoint (but called with 'DELETE').
            return fileUpload.apiPath;
        }

        // Fall back to default paths.
        return null;
    }

    /**
     * Initializes the drawing handler for attachment drawing functionality.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #initializeDrawingHandler() {
        // Try to get top document, but fall back to current document if cross-origin.
        let topDocument = document;

        try {
            if (window.top && window.top.document) {
                topDocument = window.top.document;
            }
        } catch (e) {
            // Cross-origin, use current document.
            topDocument = document;
        }
        
        // Check if drawing dialog already exists at top level.
        let drawingDialog = topDocument.querySelector('.drawing-dialog');
        let drawingContainer = null;

        if (!drawingDialog) {
            // Create native dialog element.
            drawingDialog = topDocument.createElement('dialog');
            drawingDialog.className = 'drawing-dialog';
            
            // Create drawing container structure inside dialog.
            drawingContainer = topDocument.createElement('div');
            drawingContainer.className = 'drawing-overlay';
            drawingContainer.innerHTML = `
                <div class="drawing-tools tools-right">
                    <button type="button" class="button button-tall button-icon-only tool-button close-tool">
                        <span class="button-icon lf-icon-close"></span>
                    </button>
                    <button type="button" class="button button-tall button-icon-only tool-button save-tool">
                        <span class="button-icon lf-icon-check"></span>
                    </button>
                </div>
                <div class="canvas-wrapper">
                    <canvas class="drawing-canvas"></canvas>
                    <img class="canvas-sizer" />
                </div>
                <div class="drawing-tools">
                    <div class="tools-container">
                        <button type="button" class="button button-tall button-icon-only tool-button undo-tool">
                            <span class="button-icon lf-icon-undo"></span>
                        </button>
                        <button type="button" class="color-picker" style="background-color: hsl(202deg 80% 50%);"></button>
                        <button type="button" class="color-picker" style="background-color: hsl(57deg 80% 50%);"></button>
                        <button type="button" class="color-picker" style="background-color: hsl(0deg 80% 50%);"></button>
                        <button type="button" class="color-picker" style="background-color: hsl(100deg 80% 50%);"></button>
                    </div>
                </div>
            `;
            
            // Add container to dialog and dialog to top document body.
            drawingDialog.appendChild(drawingContainer);
            topDocument.body.appendChild(drawingDialog);
        }
        else {
            drawingContainer = drawingDialog.querySelector('.drawing-overlay');
        }

        // Create drawing handler object using native canvas.
        this.#drawingHandler = this.#createDrawingHandler(drawingContainer || drawingDialog.querySelector('.drawing-overlay'), topDocument);
    }

    /**
     * Removes attachment from UI and dispatches removal event.
     * @param {HTMLElement} container The attachment container element.
     * @param {string} displayName The display name of the attachment.
     * @param {boolean} isNew Whether this is a new attachment.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #removeAttachmentFromUI(container, displayName, isNew) {
        // Remove from GUID map if it exists.
        // Check the element's URL to find its GUID.
        let attachmentUrl = null;
        const displayNameLink = container.querySelector('.display-name');
        
        if (displayNameLink && displayNameLink.href) {
            attachmentUrl = displayNameLink.href;
        }
        else if (container.dataset.attachmentUrl) {
            attachmentUrl = container.dataset.attachmentUrl;
        }
        
        if (attachmentUrl) {
            try {
                const url = new URL(attachmentUrl);
                const guid = url.searchParams.get('guid');
                
                if (guid) {
                    this.#guidToElementMap.delete(guid);
                }
            }
            catch (error) {
                // Invalid URL, ignore.
            }
        }
        
        container.remove();

        // Update scroll width for stacked view.
        if (this.stacked) {
            this.#updateScrollWidth();
        }

        // Dispatch attachment removed event.
        this.dispatchEvent(new CustomEvent('attachment-removed', {
            bubbles: true,
            detail: {
                displayName: displayName,
                isNew: isNew
            }
        }));
    }

    /**
     * Sets up mouse drag scrolling functionality.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #setupDragScrolling() {
        const container = this.#attachmentContainer;

        let isDragging = false;
        let scrollLeft = 0;
        let startX = 0;

        // Mouse down - start drag.
        container.addEventListener('mousedown', (event) => {
            // Only start drag if not clicking on a button/control or previewable card.
            if (event.target.closest('.preview-controls') || event.target.closest('button') || event.target.closest('a') || event.target.closest('.preview-card.previewable')) {
                return;
            }

            isDragging = true;
            container.classList.add('dragging');
            startX = event.pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;

            // Prevent text selection during drag.
            event.preventDefault();
        });

        // Mouse leave - stop drag.
        container.addEventListener('mouseleave', () => {
            isDragging = false;
            container.classList.remove('dragging');
        });

        // Mouse up - stop drag.
        container.addEventListener('mouseup', () => {
            isDragging = false;
            container.classList.remove('dragging');
        });

        // Mouse move - perform drag scroll.
        container.addEventListener('mousemove', (event) => {
            if (!isDragging) {
                return;
            }

            event.preventDefault();
            const x = event.pageX - container.offsetLeft;

            // Multiply by 2 for faster scrolling.
            const walk = (x - startX) * 2;

            container.scrollLeft = scrollLeft - walk;
        });

        // Set cursor styles on hover (only if scrollable).
        container.addEventListener('mouseenter', () => {
            if (container.scrollWidth > container.clientWidth) {
                container.classList.add('scrollable');
            }
        });
    }

    /**
     * Sets up horizontal scrolling for desktop mouse wheel events and drag scrolling.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #setupHorizontalScrolling() {
        if (!this.#attachmentContainer) {
            return;
        }

        // Convert vertical mouse wheel to horizontal scroll.
        this.#attachmentContainer.addEventListener('wheel', (event) => {
            // Only handle if scrolling vertically and container can scroll horizontally.
            if (event.deltaY !== 0 && this.#attachmentContainer.scrollWidth > this.#attachmentContainer.clientWidth) {
                event.preventDefault();
                
                // Convert vertical wheel delta to horizontal scroll.
                const scrollAmount = event.deltaY;
                this.#attachmentContainer.scrollLeft += scrollAmount;
            }
        }, { passive: false });

        // Handle horizontal scrolling with Shift+wheel (common desktop pattern).
        this.#attachmentContainer.addEventListener('wheel', (event) => {
            if (event.shiftKey && event.deltaY !== 0) {
                event.preventDefault();
                this.#attachmentContainer.scrollLeft += event.deltaY;
            }
        }, { passive: false });

        // Add drag scrolling functionality.
        this.#setupDragScrolling();
    }

    /**
     * Sets up mobile touch handling for stacked preview cards.
     * @param {HTMLElement} card The preview card element.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #setupMobileTouchHandling(card) {
        let touchStartTime = 0;
        let hasTouchMoved = false;

        // Touch start - record time and reset move flag.
        card.addEventListener('touchstart', () => {
            touchStartTime = Date.now();
            hasTouchMoved = false;
        }, { passive: true });

        // Touch move - set move flag.
        card.addEventListener('touchmove', () => {
            hasTouchMoved = true;
        }, { passive: true });

        // Touch end - handle tap vs scroll.
        card.addEventListener('touchend', (event) => {
            const touchDuration = Date.now() - touchStartTime;

            // If this was a quick tap (not a scroll), handle the interaction.
            if (!hasTouchMoved && touchDuration < 300) {
                // If controls are already visible, allow normal button clicks.
                if (card.classList.contains('touch-active')) {
                    // Let the button click events handle themselves.
                    return;
                }

                // First tap - show controls, prevent other actions.
                event.preventDefault();
                event.stopPropagation();

                // Clear active state from other cards.
                const allCards = this.#attachmentContainer.querySelectorAll('.preview-card');

                allCards.forEach((card) => {
                    card.classList.remove('touch-active');

                    // Clear any existing timeouts stored on cards.
                    if (card.resetTimeout) {
                        clearTimeout(card.resetTimeout);

                        delete card.resetTimeout;
                    }
                });

                // Activate this card.
                card.classList.add('touch-active');

                // Set timeout to reset card directly.
                card.resetTimeout = setTimeout(() => {
                    card.classList.remove('touch-active');

                    delete card.resetTimeout;
                }, PreviewContainer.TOUCH_TIMEOUT);
            }
        }, { passive: false });

        // Hide controls when tapping elsewhere.
        const onHandleOutsideTap = (event) => {
            if (!card.contains(event.target)) {
                card.classList.remove('touch-active');

                if (card.resetTimeout) {
                    clearTimeout(card.resetTimeout);

                    delete card.resetTimeout;
                }
            }
        };

        // Use a slight delay to attach the document listener to avoid immediate trigger.
        setTimeout(() => {
            document.addEventListener('touchstart', onHandleOutsideTap, { passive: true });
        }, 100);
    }

    /**
     * Updates the scroll width for the pseudo-element based on card count.
     * 
     * @private
     * @memberof PreviewContainer
     */
    #updateScrollWidth() {
        if (!this.stacked) {
            return;
        }

        const cards = this.#attachmentContainer.querySelectorAll('.preview-card');
        const cardCount = cards.length;
        
        if (cardCount === 0) {
            this.#attachmentContainer.style.setProperty('--scroll-width', '0');

            return;
        }

        // Read CSS custom properties.
        const cardOffset = this.parseCssValue('--card-offset', 4);
        const cardSize = this.parseCssValue('--card-size', 6);
        
        const lastCardPosition = cardCount <= 6 ? (cardCount - 1) * cardOffset : 6 * cardOffset;
        const totalWidth = lastCardPosition + cardSize;
        
        this.#attachmentContainer.style.setProperty('--scroll-width', `${totalWidth}rem`);
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

// Register the custom element.
customElements.define(PreviewContainer.tagName, PreviewContainer);
